/**
 * Magic Link検証ページ
 * メール内のリンクをクリックした際の認証処理とDAO的な成功体験を提供
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import LoadingSpinner from '../components/LoadingSpinner.jsx';
// API URL設定を直接埋め込み（Vercel対応）
const getApiUrl = () => {
  const RAILWAY_API = import.meta.env.VITE_API_URL || 'https://registration-app-production.up.railway.app';
  const LOCALHOST_API = 'http://localhost:3000';

  const isLocalhost =
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1';

  return isLocalhost ? LOCALHOST_API : RAILWAY_API;
};
const API_URL = getApiUrl();

// JWT手動デコード（jwt-decodeライブラリの代替）
function decodeJWT(token) {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      throw new Error('Invalid JWT token format');
    }

    // Base64URLデコード
    const base64Url = parts[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const padding = base64.length % 4;
    const paddedBase64 = padding ? base64 + '='.repeat(4 - padding) : base64;

    const jsonPayload = decodeURIComponent(
      window
        .atob(paddedBase64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );

    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('JWT decode error:', error);
    throw new Error('Failed to decode JWT token');
  }
}

const VerifyMagicLink = () => {
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [message, setMessage] = useState('');
  const [userInfo, setUserInfo] = useState(null);
  const [debugInfo, setDebugInfo] = useState(null); // デバッグ情報表示用
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // トークンの取得とデコード処理を強化
  let token = searchParams.get('token');

  // OutlookやメールクライアントによるURL変更への対処
  if (token) {
    try {
      let processedToken = token;

      // 1. URLデコードを試行
      const decodedToken = decodeURIComponent(token);
      if (decodedToken !== token) {
        // URLデコード完了
        processedToken = decodedToken;
      }

      // 2. Outlookの安全リンク処理の除去（もしあれば）
      if (processedToken.includes('safelinks.protection.outlook.com')) {
        const urlMatch = processedToken.match(/url=([^&]+)/);
        if (urlMatch) {
          const extractedUrl = decodeURIComponent(urlMatch[1]);
          const tokenMatch = extractedUrl.match(/[?&]token=([^&]+)/);
          if (tokenMatch) {
            processedToken = tokenMatch[1];
          }
        }
      }

      token = processedToken;
    } catch (e) {
      console.error('トークン処理エラー:', e);
    }
  }
  const hasCalledAPI = useRef(false); // API重複呼び出し防止

  // 既にログイン済みの場合は即座にダッシュボードへリダイレクト
  useEffect(() => {
    const existingToken = localStorage.getItem('token');
    const existingUser = localStorage.getItem('user');

    if (existingToken && existingUser) {
      // トークンの有効性を確認
      try {
        const decoded = decodeJWT(existingToken);
        const now = Date.now() / 1000;
        if (decoded.exp && decoded.exp > now) {
          navigate('/dashboard', { replace: true });
          return;
        } else {
          // 既存トークンが期限切れ、認証を続行
          localStorage.removeItem('token');
          localStorage.removeItem('user');
        }
      } catch {
        // 既存トークンが無効、認証を続行
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    }
  }, [navigate]);

  const verifyMagicLink = useCallback(async (magicToken) => {
    try {
      setStatus('loading');

      const apiUrl = `${API_URL}/api/auth/verify-magic-link?token=${magicToken}`;

      const response = await fetch(apiUrl);

      const data = await response.json();
      
      console.log('🔍 Magic Link検証レスポンス:', { status: response.status, data });

      if (response.ok) {
        // 認証成功
        setStatus('success');
        setMessage(data.message);
        setUserInfo(data.user);

        // JWTトークンとユーザー情報をローカルストレージに保存
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        if (data.actor) {
          localStorage.setItem('actorId', data.actor.actor_id);
        }

        // 認証成功記録（デバッグ用）

        // App-NoWallet.jsxの状態同期のためのカスタムイベント発火
        // eslint-disable-next-line no-undef
        const authUpdateEvent = new CustomEvent('authUpdated', {
          detail: {
            token: data.token,
            user: data.user,
            actor: data.actor,
          },
        });
        window.dispatchEvent(authUpdateEvent);

        // Magic Link認証成功、リダイレクト先またはダッシュボードへ遷移
        const redirectTo = searchParams.get('redirect') || '/dashboard';
        setTimeout(() => {
          try {
            if (typeof navigate === 'function') {
              navigate(redirectTo, { replace: true });
            } else {
              console.warn(
                'navigate function not available, using window.location'
              );
              window.location.href = redirectTo;
            }
          } catch (error) {
            console.error('Navigation error:', error);
            // 最終フォールバック
            window.location.href = '/dashboard';
          }
        }, 800);
      } else {
        // 認証失敗
        setStatus('error');
        const errorMessage = data.message || 'リンクの検証に失敗しました。';
        setMessage(errorMessage);

        // ユーザー未登録エラーの場合の処理
        if (
          data.errorCode === 'USER_NOT_FOUND' ||
          data.action === 'REDIRECT_TO_REGISTRATION'
        ) {
          // 未登録ユーザーです。信頼の種を植える儀式へリダイレクトします。
          setMessage(errorMessage);
          // 2秒後に登録画面へリダイレクト
          setTimeout(() => {
            navigate(data.redirectTo || '/signin', {
              replace: true,
              state: { email: data.email }, // メールアドレスを渡す
            });
          }, 2000);
        }
        // 使用済みリンクエラーの場合の処理
        else if (
          errorMessage.includes('既に使用されています') ||
          errorMessage.includes('already used') ||
          data.error === 'TOKEN_ALREADY_USED'
        ) {
          const existingToken = localStorage.getItem('token');
          const existingUser = localStorage.getItem('user');

          if (existingToken && existingUser) {
            // 使用済みリンクですが、既にログイン済みです。即座にダッシュボードに遷移します。
            // トークンが有効か簡易チェック
            try {
              const decoded = decodeJWT(existingToken);
              const now = Date.now() / 1000;
              if (decoded.exp && decoded.exp > now) {
                // 有効なトークンが存在する場合はトップページに遷移してログイン状態を確認
                navigate('/', { replace: true });
                return; // この時点でリターンして処理を終了
              } else {
                // 既存トークンが期限切れ
                localStorage.removeItem('token');
                localStorage.removeItem('user');
              }
            } catch {
              // 既存トークンの検証に失敗
              localStorage.removeItem('token');
              localStorage.removeItem('user');
            }
          }

          // トークンが無いか無効な場合は新しいMagic Linkを促す
          setMessage(
            'このリンクは既に使用されています。新しいMagic Linkを取得してください。'
          );
        }
      }
    } catch (error) {
      console.error('❌ Magic Link検証エラー:', error);
      setStatus('error');
      setMessage(
        'ネットワークエラーが発生しました。インターネット接続を確認してください。'
      );
    }
  }, [navigate]);

  // Magic Link検証を実行するuseEffect
  useEffect(() => {
    const initializeAuth = async () => {
      // URLのデバッグ情報を収集
      const debugData = {
        fullURL: window.location.href,
        searchParams: window.location.search,
        token: token,
        tokenLength: token ? token.length : 0,
        allParams: Object.fromEntries(searchParams.entries()),
        userAgent: navigator.userAgent.substring(0, 100) + '...',
      };

      setDebugInfo(debugData);

      if (!token) {
        setStatus('error');
        setMessage('🚫 無効なリンクです。Magic Linkトークンが見つかりません。');
        return;
      }

      // API重複呼び出し防止
      if (hasCalledAPI.current) {
        console.log('🔍 既にAPI呼び出し済み - スキップ');
        return;
      }

      hasCalledAPI.current = true;

      // 既存トークンがあれば検証（async/await で同期的に処理）
      const existingToken = localStorage.getItem('token');
      if (existingToken) {
        console.log('🔍 既存トークン検証開始');
        try {
          const res = await fetch(`${API_URL}/api/auth/me`, {
            headers: { Authorization: `Bearer ${existingToken}` },
          });

          if (res.ok) {
            console.log('✅ 既存トークン有効 - ダッシュボードへリダイレクト');
            navigate('/dashboard', { replace: true });
            return;
          } else {
            console.log('⚠️ 既存トークン無効 - クリアしてMagic Link検証へ');
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            localStorage.removeItem('actorId');
          }
        } catch (err) {
          console.log('❌ 既存トークン検証エラー - クリアしてMagic Link検証へ:', err);
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          localStorage.removeItem('actorId');
        }
      }

      // Magic Link検証を実行
      console.log('🔍 Magic Link検証開始 token=', token);
      verifyMagicLink(token);
    };

    initializeAuth();
  }, [token, navigate, verifyMagicLink]);

  const handleRetry = () => {
    if (token) {
      verifyMagicLink(token);
    }
  };

  const handleBackToLogin = () => {
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-blue-50 to-purple-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-2xl p-8">
        {/* ヘッダー */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent mb-2">
            🌱 真決DAO
          </h1>
          <div className="w-16 h-1 bg-gradient-to-r from-green-400 to-blue-400 mx-auto rounded-full"></div>
        </div>

        {/* ローディング状態 */}
        {status === 'loading' && (
          <LoadingSpinner message="✨ 信頼の扉を開いています..." />
        )}

        {/* 成功状態 */}
        {status === 'success' && (
          <div className="text-center space-y-6">
            <div className="text-6xl animate-bounce">🎉</div>
            <div>
              <h2 className="text-xl font-semibold text-green-800 mb-2">
                森の扉が開かれました！
              </h2>
              <p className="text-green-700 mb-4">{message}</p>

              {userInfo && (
                <div className="bg-green-50 p-4 rounded-lg border border-green-200 text-sm">
                  <p className="font-medium text-green-800 mb-2">
                    🌿 あなたの森の住人情報
                  </p>
                  <div className="text-green-700 space-y-1">
                    <p>名前: {userInfo.username || userInfo.email}</p>
                    <p>
                      権限レベル:{' '}
                      {userInfo.permission_level === 2
                        ? '準会員'
                        : `レベル${userInfo.permission_level}`}
                    </p>
                    <p>
                      状態:{' '}
                      {userInfo.kyc_status === 'pending'
                        ? '🌱 信頼育成中'
                        : userInfo.kyc_status}
                    </p>

                    {/* トークンデバッグ情報 */}
                    <div className="mt-3 p-2 bg-blue-50 border border-blue-200 rounded text-xs">
                      <p className="font-medium text-blue-800 mb-1">
                        🔍 トークン情報
                      </p>
                      <p className="text-blue-700">
                        LocalStorage Token:{' '}
                        {localStorage.getItem('token')
                          ? localStorage.getItem('token').substring(0, 20) +
                            '...'
                          : 'なし'}
                      </p>
                      <p className="text-blue-700">
                        LocalStorage User:{' '}
                        {JSON.stringify(
                          JSON.parse(localStorage.getItem('user') || '{}')
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <div className="animate-pulse">
                <div className="animate-spin rounded-full h-6 w-6 border-2 border-green-600 border-t-transparent mx-auto mb-2"></div>
                <p className="text-gray-600 text-sm">
                  まもなくダッシュボードへ...
                </p>
              </div>

              <button
                onClick={() => navigate('/dashboard')}
                className="w-full bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 text-white font-bold py-3 px-6 rounded-full transition-all duration-300 transform hover:scale-105"
              >
                🚀 森の世界へ今すぐ入る
              </button>
            </div>
          </div>
        )}

        {/* エラー状態 */}
        {status === 'error' && (
          <div className="text-center space-y-6">
            <div className="text-6xl">😔</div>
            <div>
              <h2 className="text-xl font-semibold text-red-800 mb-2">
                扉が開けませんでした
              </h2>
              <p className="text-red-700 mb-4">{message}</p>

              <div className="bg-red-50 p-4 rounded-lg border border-red-200 text-sm">
                <p className="font-medium text-red-800 mb-2">
                  🔍 考えられる原因
                </p>
                <ul className="text-red-700 text-left space-y-1">
                  <li>• リンクの有効期限が切れている（30分間有効）</li>
                  <li>• 既に一度使用されたリンク</li>
                  <li>• ネットワーク接続に問題がある</li>
                  <li>
                    • <strong>Outlookからのアクセス</strong>
                    ：リンクを直接開かず、リンクをコピーしてブラウザのアドレスバーに貼り付けてください
                  </li>
                </ul>

                {localStorage.getItem('token') && (
                  <div className="mt-3 p-2 bg-green-50 border border-green-200 rounded">
                    <p className="font-medium text-green-800 text-xs">
                      💡
                      既にログイン済みです。上の「ダッシュボードへ進む」ボタンをクリックしてください。
                    </p>
                  </div>
                )}

                {/* デバッグ情報セクション（スマホ対応） */}
                {debugInfo && (
                  <div className="mt-4 bg-gray-50 p-3 rounded-lg border border-gray-200 text-xs">
                    <p className="font-medium text-gray-800 mb-2">
                      🔍 デバッグ情報（開発者用）
                    </p>
                    <div className="text-gray-600 space-y-1 text-left">
                      <p>
                        <strong>URL:</strong>{' '}
                        <span className="break-all">{debugInfo.fullURL}</span>
                      </p>
                      <p>
                        <strong>検索パラメータ:</strong>{' '}
                        {debugInfo.searchParams || '(なし)'}
                      </p>
                      <p>
                        <strong>トークン:</strong>{' '}
                        {debugInfo.token
                          ? `${debugInfo.token.substring(0, 20)}... (長さ: ${debugInfo.tokenLength})`
                          : '(見つかりません)'}
                      </p>
                      <p>
                        <strong>全パラメータ:</strong>{' '}
                        {JSON.stringify(debugInfo.allParams)}
                      </p>
                      <p>
                        <strong>ブラウザ:</strong> {debugInfo.userAgent}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-2">
              {/* 既にログイン済みの場合はダッシュボードボタンを優先表示 */}
              {localStorage.getItem('token') ? (
                <button
                  onClick={() => navigate('/dashboard')}
                  className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-6 rounded-full transition-all duration-300 mb-2"
                >
                  🚀 ダッシュボードへ進む
                </button>
              ) : null}

              <button
                onClick={handleRetry}
                className="w-full bg-yellow-600 hover:bg-yellow-700 text-white font-bold py-3 px-6 rounded-full transition-all duration-300"
              >
                🔄 もう一度試す
              </button>

              <button
                onClick={handleBackToLogin}
                className="w-full bg-gray-600 hover:bg-gray-700 text-white font-bold py-3 px-6 rounded-full transition-all duration-300"
              >
                🏠 最初からやり直す
              </button>
            </div>
          </div>
        )}

        {/* フッター */}
        <div className="mt-8 text-center">
          <p className="text-xs text-gray-500">
            問いと共鳴が、誰かの行動を生み出し、
            <br />
            その行動が、あなたの暮らしを支える場
          </p>
        </div>
      </div>
    </div>
  );
};

export default VerifyMagicLink;
